import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Branch, BranchDay, SaveBranchRequest } from '../api/types';
import { Alert, Spinner } from '../components/common';
import { useAuth } from '../state/AuthContext';

/**
 * The shops.
 *
 * One branch is the default: it is where an order lands when the customer did
 * not pick, and it is the only one that cannot be closed. Everything else about
 * a shop — its address, its phone, its week — belongs to it alone, which is the
 * whole point of the screen.
 *
 * There is no delete. A branch owns the orders it took and those have to keep
 * pointing somewhere, so closing takes it out of the customer's picker and
 * leaves its history readable. Same reasoning as closing a customer account.
 */

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** A new shop starts on the hours every branch keeps today. */
function defaultWeek(): BranchDay[] {
  return DAYS.map((day) => ({
    day,
    opens: '09:00',
    closes: day === 'Saturday' ? '17:00' : day === 'Sunday' ? '15:00' : '19:00',
  }));
}

function blank(): SaveBranchRequest {
  return {
    name: '',
    addressLine: '',
    suburb: '',
    phone: '',
    email: '',
    offersCollection: true,
    offersDelivery: true,
    lastOrderMinutesBeforeClose: 20,
    week: defaultWeek(),
  };
}

function toForm(b: Branch): SaveBranchRequest {
  return {
    name: b.name,
    addressLine: b.addressLine,
    suburb: b.suburb,
    phone: b.phone,
    email: b.email,
    offersCollection: b.offersCollection,
    offersDelivery: b.offersDelivery,
    lastOrderMinutesBeforeClose: b.lastOrderMinutesBeforeClose,
    week: b.week,
  };
}

export function StaffBranchesPage() {
  const { customer: me } = useAuth();
  const canManage = me?.role === 'Admin' || me?.role === 'SuperAdmin';

  const [branches, setBranches] = useState<Branch[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // null = not editing; a branch id = editing that one; 'new' = creating.
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<SaveBranchRequest>(blank);

  const load = useCallback(async () => {
    try {
      setBranches(await api.branchesAll());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);

    try {
      await action();
      await load();
      setEditing(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function startNew() {
    setForm(blank());
    setEditing('new');
    setError(null);
  }

  function startEdit(b: Branch) {
    setForm(toForm(b));
    setEditing(b.id);
    setError(null);
  }

  function setDay(day: string, field: 'opens' | 'closes', value: string) {
    setForm((f) => ({
      ...f,
      week: (f.week ?? defaultWeek()).map((d) =>
        d.day === day ? { ...d, [field]: value === '' ? null : value } : d,
      ),
    }));
  }

  function toggleClosed(day: string, closed: boolean) {
    setForm((f) => ({
      ...f,
      week: (f.week ?? defaultWeek()).map((d) =>
        d.day === day ? { ...d, opens: closed ? null : '09:00', closes: closed ? null : '17:00' } : d,
      ),
    }));
  }

  if (!branches) return <Spinner />;

  return (
    <section className="page">
      <div className="branches__head">
        <div>
          <h1>Branches</h1>
          <p className="muted">
            Each shop keeps its own address, phone number and trading hours. Orders land at the
            default branch unless the customer picks another.
          </p>
        </div>
        {canManage && editing === null && (
          <button type="button" className="btn btn--primary" onClick={startNew}>
            Open a branch
          </button>
        )}
      </div>

      {error && <Alert>{error}</Alert>}

      {editing !== null && (
        <div className="panel">
          <h2 className="panel__title">{editing === 'new' ? 'A new shop' : 'Edit this shop'}</h2>

          <div className="form-row">
            <label className="field">
              <span>Name</span>
              <input
                value={form.name}
                placeholder="Sandton City"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Suburb</span>
              <input
                value={form.suburb}
                placeholder="Sandton"
                onChange={(e) => setForm({ ...form, suburb: e.target.value })}
              />
            </label>
          </div>

          <label className="field">
            <span>Address</span>
            <input
              value={form.addressLine}
              placeholder="Shop 42, Sandton City, Rivonia Road, Sandhurst"
              onChange={(e) => setForm({ ...form, addressLine: e.target.value })}
            />
          </label>

          <div className="form-row">
            <label className="field">
              <span>Phone</span>
              <input
                value={form.phone}
                placeholder="+27 72 982 0176"
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                placeholder="sandton@e-smoothies.co.za"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </label>
          </div>

          <div className="form-row">
            <label className="check">
              <input
                type="checkbox"
                checked={form.offersCollection}
                onChange={(e) => setForm({ ...form, offersCollection: e.target.checked })}
              />
              <span>Takes collection orders</span>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={form.offersDelivery}
                onChange={(e) => setForm({ ...form, offersDelivery: e.target.checked })}
              />
              <span>Delivers</span>
            </label>
          </div>

          <label className="field">
            <span>Stop taking orders this many minutes before closing</span>
            <input
              type="number"
              min={0}
              max={120}
              value={form.lastOrderMinutesBeforeClose}
              onChange={(e) =>
                setForm({ ...form, lastOrderMinutesBeforeClose: Number(e.target.value) })
              }
            />
          </label>

          <h3 className="section-heading">Trading hours</h3>
          <div className="hours">
            {DAYS.map((day) => {
              const entry = (form.week ?? defaultWeek()).find((d) => d.day === day);
              const closed = !entry?.opens;

              return (
                <div key={day} className="hours__row">
                  <span className="hours__day">{day.slice(0, 3)}</span>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={closed}
                      onChange={(e) => toggleClosed(day, e.target.checked)}
                    />
                    <span>Closed</span>
                  </label>
                  {!closed && (
                    <>
                      <input
                        type="time"
                        value={entry?.opens ?? ''}
                        onChange={(e) => setDay(day, 'opens', e.target.value)}
                      />
                      <span className="muted">to</span>
                      <input
                        type="time"
                        value={entry?.closes ?? ''}
                        onChange={(e) => setDay(day, 'closes', e.target.value)}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rolebtns">
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || !form.name.trim()}
              onClick={() =>
                run(() =>
                  editing === 'new' ? api.branchCreate(form) : api.branchUpdate(editing, form),
                )
              }
            >
              {busy ? 'Saving…' : editing === 'new' ? 'Open this branch' : 'Save changes'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {branches.map((b) => (
        <div key={b.id} className={`panel branch ${b.isActive ? '' : 'branch--closed'}`}>
          <div className="branch__head">
            <div>
              <h2 className="panel__title">
                {b.name}{' '}
                {b.isDefault && <span className="pill pill--good">Default</span>}{' '}
                {!b.isActive && <span className="pill pill--muted">Closed</span>}{' '}
                {b.isActive && (
                  <span className={`pill ${b.isOpenNow ? 'pill--good' : 'pill--muted'}`}>
                    {b.isOpenNow ? 'Open now' : 'Shut now'}
                  </span>
                )}
              </h2>
              <p className="muted small">
                {b.addressLine} · <a href={`tel:${b.phone}`}>{b.phone}</a> ·{' '}
                <a href={`mailto:${b.email}`}>{b.email}</a>
              </p>
              <p className="muted small">
                {b.hoursSummary} · last orders {b.lastOrderMinutesBeforeClose} min before close
              </p>
              <p className="muted small">
                {b.offersCollection ? 'Collection' : 'No collection'} ·{' '}
                {b.offersDelivery ? 'Delivery' : 'No delivery'} · {b.orderCount} order
                {b.orderCount === 1 ? '' : 's'}
              </p>
            </div>
          </div>

          {canManage && (
            <div className="rolebtns">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={busy}
                onClick={() => startEdit(b)}
              >
                Edit
              </button>

              {!b.isDefault && b.isActive && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={busy}
                  onClick={() => run(() => api.branchSetDefault(b.id))}
                >
                  Make default
                </button>
              )}

              {/* The default cannot be closed — the API refuses too, and says why. */}
              {!b.isDefault && (
                <button
                  type="button"
                  className={b.isActive ? 'btn btn--danger btn--sm' : 'btn btn--ghost btn--sm'}
                  disabled={busy}
                  onClick={() => run(() => api.branchSetActive(b.id, !b.isActive))}
                >
                  {b.isActive ? 'Close this branch' : 'Reopen'}
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
